import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {StringUtils} from '@application/helper/string-utils';
import {MachineType} from '@domain/machine/machine-type.enum';
import {ProductionOrderCarpetForPlanningOrder} from '@domain/planning/carpet/production-order-carpet-for-planning-order';
import {NeedsAttentionItem} from '@domain/planning/needs-attention-item';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningOrder} from '@domain/planning/planning-order';
import {PlanningOrderData} from '@domain/planning/planning-order-data';
import {ProductionOrderTuftingForPlanningOrder} from '@domain/planning/tufting/production-order-tufting-for-planning-order';
import {ProductionOrderWeavingForPlanningOrder} from '@domain/planning/weaving/production-order-weaving-for-planning-order';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {PLANNING, Planning} from '@infrastructure/http/planning/planning';
import {PRODUCTION_ORDERS_WEAVING, ProductionOrdersWeaving} from '@infrastructure/http/production-order-weaving/production-orders-weaving';
import {PRODUCTION_ORDERS, ProductionOrders} from '@infrastructure/http/production-order/production-orders';
import {HttpTuftProductionOrdersService} from '@infrastructure/http/tuft-production-order/http-tuft-production-orders.service';
import {
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  DialogType,
  GridOptionsBuilderFactoryService,
  moment,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  SelectGridDialog,
  TimeUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, GridApi, GridOptions} from 'ag-grid-community';
import {each, isEmpty, isNil, join, lowerCase, map, size} from 'lodash-es';
import {forkJoin, Observable, of as observableOf} from 'rxjs';
import {catchError, map as rxjsMap, takeUntil} from 'rxjs/operators';
import {SelectProductionOrderParameters} from './select-production-order-parameters';

@Component({
  selector: 'app-select-production-order',
  templateUrl: './select-production-order.component.html',
  styleUrls: ['./select-production-order.component.scss']
})
export class SelectProductionOrderComponent extends BaseComponent implements OnInit, AfterViewInit, SelectGridDialog {
  @ViewChild('productionOrdersGrid') public productionOrdersGrid: AgGridAngular;
  public listOfGridOptions: GridOptions[];
  public listOfGridApis: GridApi[];
  public listOfProductionOrders: PlanningOrderData[];

  private readonly machineQualityId: number;
  private readonly creelId: number;
  private readonly weaveProductId: number;
  private readonly tuftProductId: number;
  private readonly machine: PlanningMachine;
  private readonly selectedProductionOrders: PlanningOrderData[];

  public constructor(
    @Inject(PRODUCTION_ORDERS) private readonly productionOrders: ProductionOrders,
    @Inject(PRODUCTION_ORDERS_WEAVING) private readonly productionOrdersWeaving: ProductionOrdersWeaving,
    @Inject(PLANNING) private readonly planning: Planning,
    @Inject(MAT_DIALOG_DATA)
    {machineQualityId, creelId, weaveProductId, machine, selectedProductionOrders, tuftProductId}: SelectProductionOrderParameters,
    private readonly dialogRef: MatDialogRef<SelectProductionOrderComponent>,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly productionOrdersTufting: HttpTuftProductionOrdersService
  ) {
    super();

    this.machineQualityId = machineQualityId;
    this.creelId = creelId;
    this.weaveProductId = weaveProductId;
    this.machine = machine;
    this.selectedProductionOrders = selectedProductionOrders;
    this.tuftProductId = tuftProductId;
  }

  public get gridOptions(): GridOptions {
    return this.listOfGridOptions[0];
  }

  public ngOnInit(): void {
    this.setGridOptions();
    this.planning
      .getOrdersThatRequireAttention()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((needsAttentionItems: NeedsAttentionItem[]) => {
        if (this.machine.machineType === MachineType.WEAVING_MACHINE) {
          this.listOfProductionOrders = this.getProductionOrdersWeavingThatNeedAttention(needsAttentionItems);
        } else if (this.machine.machineType === MachineType.TUFT) {
          this.listOfProductionOrders = this.getProductionOrdersTuftingThatNeedAttention(needsAttentionItems);
        } else {
          this.listOfProductionOrders = this.getProductionOrdersCarpetThatNeedAttention(needsAttentionItems);
        }
      });
  }

  public ngAfterViewInit(): void {
    this.listOfGridApis = [this.productionOrdersGrid.api];
  }

  public filterProductionOrders(filterText: string): void {
    if (!isEmpty(this.listOfProductionOrders)) {
      this.productionOrdersGrid.api.setGridOption('quickFilterText', filterText);
    }
  }

  public canSelectProductionOrders(): boolean {
    return this.productionOrdersGrid?.api?.getSelectedRows().length > 0;
  }

  public selectProductionOrders(): void {
    if (!this.canSelectProductionOrders()) {
      return;
    }
    const selectedOrders = this.productionOrdersGrid.api.getSelectedRows() as PlanningOrderData[];

    forkJoin(map(selectedOrders, (order: PlanningOrderData) => this.getCheckCompatibilityWithMachineRequestForOrder(order)))
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((results: {order: PlanningOrderData; durationInMs: number}[]) => {
        const compatiblePlanningOrders: PlanningOrder[] = [];
        const incompatibleProductionOrderNames: string[] = [];

        each(results, (result: {order: PlanningOrderData; durationInMs: number}) => {
          if (isNil(result.durationInMs)) {
            incompatibleProductionOrderNames.push(result.order.name);
          } else {
            result.order.status = ProductionScheduleStatus.TO_RECONFIRM;

            compatiblePlanningOrders.push(new PlanningOrder(null, new Date(), moment().add(result.durationInMs, TimeUtils.MILLISECONDS_UNIT).toDate(), null, result.order));
          }
        });

        if (!isEmpty(incompatibleProductionOrderNames)) {
          this.showAlertErrorForIncompatibleProductionOrders(incompatibleProductionOrderNames);
        }

        if (!isEmpty(compatiblePlanningOrders)) {
          this.dialogRef.close(compatiblePlanningOrders);
        }
      });
  }

  private getProductionOrdersCarpetThatNeedAttention(needsAttentionItems: NeedsAttentionItem[]): ProductionOrderCarpetForPlanningOrder[] {
    return needsAttentionItems
      .filter((item: NeedsAttentionItem) => item.isCarpetOrder)
      .map((item: NeedsAttentionItem) => item.productionOrderCarpet)
      .filter(
        (order: ProductionOrderCarpetForPlanningOrder) =>
          order.needsToBePlanned() &&
          order.machineQuality.id === this.machineQualityId &&
          order.creel.id === this.creelId &&
          !this.selectedProductionOrders.some((orderData: PlanningOrderData) => orderData.id === order.id)
      );
  }

  private getProductionOrdersWeavingThatNeedAttention(needsAttentionItems: NeedsAttentionItem[]): ProductionOrderWeavingForPlanningOrder[] {
    return needsAttentionItems
      .filter((item: NeedsAttentionItem) => item.isWeavingOrder)
      .map((item: NeedsAttentionItem) => item.productionOrderWeaving)
      .filter(
        (order: ProductionOrderWeavingForPlanningOrder) =>
          order.needsToBePlanned() && order.weaveProduct.id === this.weaveProductId && !this.selectedProductionOrders.some((orderData: PlanningOrderData) => orderData.id === order.id)
      );
  }

  private getProductionOrdersTuftingThatNeedAttention(needsAttentionItems: NeedsAttentionItem[]): ProductionOrderTuftingForPlanningOrder[] {
    return needsAttentionItems
      .filter((item: NeedsAttentionItem) => item.isTuftingOrder)
      .map((item: NeedsAttentionItem) => item.productionOrderTufting)
      .filter(
        (order: ProductionOrderTuftingForPlanningOrder) =>
          order.needsToBePlanned() && order.tuftProduct.id === this.tuftProductId && !this.selectedProductionOrders.some((orderData: PlanningOrderData) => orderData.id === order.id)
      );
  }

  private setGridOptions(): void {
    this.listOfGridOptions = [
      this.gridOptionsBuilderFactoryService
        .getBuilder(this.getColumnDefs(), GridIdentifier.SELECT_PRODUCTION_ORDER)
        .withRowSelection(true)
        .withNoRowsOverlay({
          scale: 0.7,
          titleKey: 'PLANNING.ADD_ITEM.SELECT_PRODUCTION_ORDERS.NO_PRODUCTION_ORDERS_FOR_RUN',
          hideDescription: true
        } as Partial<NoDataOverlayComponentParams>)
        .withLoadingOverlay({
          scale: 0.7
        } as OverlayComponentParams)
        .build()
    ];
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withField('name').withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).withoutFilter().build(),
      this.colDefBuilderFactoryService.getBuilder().withField('dueDate').withHeaderName('GENERAL.DATETIME.DUE_DATE').withDate().withoutFilter().build()
    ];
  }

  private getCheckCompatibilityWithMachineRequestForOrder(order: PlanningOrderData): Observable<{order: PlanningOrderData; durationInMs: number}> {
    const failureResponse = observableOf({order, durationInMs: null});
    if (order instanceof ProductionOrderCarpetForPlanningOrder) {
      return this.productionOrders.checkCompatibilityWithMachine(order.id, this.machine.id).pipe(
        rxjsMap((durationInMs: number) => ({order, durationInMs})),
        catchError(() => failureResponse)
      );
    }
    if (order instanceof ProductionOrderWeavingForPlanningOrder) {
      return this.productionOrdersWeaving.checkCompatibilityWithMachine(order.id, this.machine.id).pipe(
        rxjsMap((durationInMs: number) => ({order, durationInMs})),
        catchError(() => failureResponse)
      );
    }
    if (order instanceof ProductionOrderTuftingForPlanningOrder) {
      return this.productionOrdersTufting.checkCompatibilityWithMachine(order.id, this.machine.id).pipe(
        rxjsMap((durationInMs: number) => ({order, durationInMs})),
        catchError(() => failureResponse)
      );
    }
    return failureResponse;
  }

  private showAlertErrorForIncompatibleProductionOrders(incompatibleProductionOrderNames: string[]): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant('PLANNING.ERRORS.INCOMPATIBLE_ENTITY', {
        entity: lowerCase(this.translate.instant('PRODUCTION_ORDER.PRODUCTION_ORDER', {count: size(incompatibleProductionOrderNames)}))
      }),
      messageText: this.translate.instant('PLANNING.ERRORS.ADD_PRODUCTION_ORDER_ERROR', {
        count: size(incompatibleProductionOrderNames),
        productionOrderName: join(incompatibleProductionOrderNames, ', ')
      }),
      type: DialogType.INFORMATION
    });
  }
}
